import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

/**
 * Where to draw one highlight that glides to the active item in a list (sidebar links, tab
 * buttons), instead of each item painting its own. `selector` finds the active item inside the
 * container; `key` changes whenever the active item may have changed.
 *
 * The first placement doesn't animate (nothing slides in from the corner); later ones do. It
 * re-measures when the container resizes, e.g. tabs wrapping onto two lines.
 */
export function useSlidingIndicator<T extends HTMLElement>(selector: string, key: unknown): {
  ref: RefObject<T>;
  style: CSSProperties | null;
} {
  const ref = useRef<T>(null);
  const [box, setBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const placed = useRef(false);

  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) return undefined;
    const measure = () => {
      const active = container.querySelector<HTMLElement>(selector);
      if (!active) {
        setBox(null);
        return;
      }
      setBox({ x: active.offsetLeft, y: active.offsetTop, w: active.offsetWidth, h: active.offsetHeight });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [selector, key]);

  useLayoutEffect(() => {
    if (box && !placed.current) {
      // Turn the glide on only after the first position has been painted.
      const frame = requestAnimationFrame(() => {
        placed.current = true;
      });
      return () => cancelAnimationFrame(frame);
    }
    return undefined;
  }, [box]);

  const style: CSSProperties | null = box
    ? {
        transform: `translate(${box.x}px, ${box.y}px)`,
        width: box.w,
        height: box.h,
        transition:
          placed.current && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), width 0.45s cubic-bezier(0.22, 1, 0.36, 1), height 0.45s ease'
            : 'none',
      }
    : null;
  return { ref, style };
}
